/**
 * Mirror of the ingestion-rule wire shapes served at `/v1/ingestion-rules`.
 *
 * A rule says which files are ingestable (path patterns) and how they are
 * ingested (an action invocation). A rule with an invocation owns one managed
 * trigger that starts on `file.uploaded`; `auto_ingest` is whether that
 * trigger is enabled.
 */

import {
  ManagedByKind,
  type ActionInvocationSpec,
  type TriggerV2Record,
} from "@/shared/domain/triggers";

export interface IngestionRuleRecord {
  rule_id: string;
  org_id: string;
  name: string;
  description: string | null;
  include_patterns: string[];
  exclude_patterns: string[];
  /**
   * How a matching file is ingested. Its `invocation_input` holds only the
   * inputs added beside the file: the managed trigger always passes the
   * uploaded file first, so the rule never stores it. Null only on a rule that
   * does not auto-ingest.
   */
  invocation: ActionInvocationSpec | null;
  auto_ingest: boolean;
  /**
   * The managed trigger this rule owns, enabled while auto-ingest is on. Null
   * while the rule has no invocation.
   */
  trigger_id: string | null;
  created: string;
  created_by: string;
  modified: string;
  modified_by: string;
}

export interface CreateIngestionRuleRequest {
  name: string;
  description?: string | null;
  include_patterns: string[];
  exclude_patterns?: string[];
  /** Required when `auto_ingest` is on. */
  invocation?: ActionInvocationSpec | null;
  auto_ingest?: boolean;
}

/**
 * One of the rules Roboto offers every org to start a rule from, as
 * `GET /v1/ingestion-rules/defaults` lists it. `already_present` says whether
 * the org has it already.
 */
export interface DefaultIngestionRule {
  name: string;
  /** The group the new-rule page lists it under, such as "ROS" or "Tabular". */
  category: string;
  include_patterns: string[];
  exclude_patterns: string[];
  invocation: ActionInvocationSpec;
  already_present: boolean;
  /**
   * Whether sign-up offers it, checked, and "Add default rules" creates it.
   * The others are only offered to start a rule from.
   */
  for_new_orgs: boolean;
  /**
   * Whether its action likely needs settings that vary with the org's files,
   * such as which column of a CSV file holds the timestamps.
   */
  needs_settings: boolean;
}

/**
 * Every field optional; an omitted field is left unchanged. `invocation`
 * replaces the whole spec when present.
 */
export type UpdateIngestionRuleRequest = Partial<CreateIngestionRuleRequest>;

/**
 * The id of the rule that manages a trigger, read off the trigger's
 * `managed_by`, or null when no rule manages it.
 */
export function ingestionRuleIdOf(
  trigger: Pick<TriggerV2Record, "managed_by">,
): string | null {
  const managedBy = trigger.managed_by;
  return managedBy?.kind === ManagedByKind.IngestionRule ? managedBy.id : null;
}
